import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useMyPermissions } from '@/hooks/useRolePermissions'
import type { PermissionKey } from '@/lib/api'
import Layout from '@/components/Layout'
import Login from '@/pages/Login'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import SetupAccount from '@/pages/SetupAccount'
import Dashboard from '@/pages/Dashboard'
import Contacts from '@/pages/Contacts'
import Deals from '@/pages/Deals'
import ExcelImport from '@/pages/ExcelImport'
import CustomerDatabase from '@/pages/CustomerDatabase'
import ServiceRequests from '@/pages/ServiceRequests'
import SiteSurveys from '@/pages/SiteSurveys'
import UserManagement from '@/pages/UserManagement'
import Profile from '@/pages/Profile'
import ActivityLogs from '@/pages/ActivityLogs'
import WeeklyReport from '@/pages/WeeklyReport'
import ProjectManagement from '@/pages/ProjectManagement'
import BudgetRequest from '@/pages/BudgetRequest'
import ExpenseClaim from '@/pages/ExpenseClaim'
import AdvanceCash from '@/pages/AdvanceCash'
import Analytics from '@/pages/Analytics'
import CrmReports from '@/pages/CrmReports'
import HandoverForms from '@/pages/HandoverForms'
import OrgChart from '@/pages/OrgChart'

const ALL_ROLES = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'chief_engineer', 'operation_manager', 'engineer', 'technician', 'finance', 'procurement', 'storekeeper', 'hr']

function ProtectedRoute({
  children,
  roles,
  permKey,
}: {
  children: React.ReactNode
  roles?: string[]
  permKey?: PermissionKey
}) {
  const { user, isLoading } = useAuth()
  const { canDo, isSuccess: permsLoaded, isLoading: permsLoading } = useMyPermissions()

  const spinner = <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800"></div></div>

  if (isLoading) return spinner
  if (!user) return <Navigate to="/login" />

  if (permKey) {
    if (permsLoading && !permsLoaded) return spinner
    if (permsLoaded && !canDo(permKey)) return <Navigate to="/dashboard" />
  } else if (roles && !roles.includes(user.role)) {
    return <Navigate to="/" />
  }

  return <>{children}</>
}

export default function App() {
  const { user, isLoading } = useAuth()

  if (isLoading) {
    return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800"></div></div>
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" /> : <Login />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/setup-account" element={<SetupAccount />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<ProtectedRoute roles={ALL_ROLES}><Dashboard /></ProtectedRoute>} />
        <Route path="contacts" element={<ProtectedRoute permKey="crm_view"><Contacts /></ProtectedRoute>} />
        <Route path="deals" element={<ProtectedRoute permKey="crm_view"><Deals /></ProtectedRoute>} />
        <Route path="import" element={<ProtectedRoute permKey="project_bank_create"><ExcelImport /></ProtectedRoute>} />
        <Route path="customers" element={<ProtectedRoute roles={['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'chief_engineer', 'operation_manager', 'engineer', 'finance', 'procurement', 'storekeeper']}><CustomerDatabase /></ProtectedRoute>} />
        <Route path="service-requests" element={<ProtectedRoute roles={ALL_ROLES}><ServiceRequests /></ProtectedRoute>} />
        <Route path="site-surveys" element={<ProtectedRoute roles={ALL_ROLES}><SiteSurveys /></ProtectedRoute>} />
        <Route path="weekly-report" element={<ProtectedRoute roles={ALL_ROLES}><WeeklyReport /></ProtectedRoute>} />
        <Route path="project-management" element={<ProtectedRoute roles={ALL_ROLES}><ProjectManagement /></ProtectedRoute>} />
        <Route path="users" element={<ProtectedRoute roles={['super_admin', 'admin']}><UserManagement /></ProtectedRoute>} />
        <Route path="org-chart" element={<ProtectedRoute roles={['super_admin', 'admin', 'director']}><OrgChart /></ProtectedRoute>} />
        <Route path="activity-logs" element={<ProtectedRoute permKey="activity_logs_view"><ActivityLogs /></ProtectedRoute>} />
        <Route path="budget-requests" element={<ProtectedRoute roles={ALL_ROLES}><BudgetRequest /></ProtectedRoute>} />
        <Route path="expense-claims" element={<ProtectedRoute roles={ALL_ROLES}><ExpenseClaim /></ProtectedRoute>} />
        <Route path="advance-cash" element={<ProtectedRoute roles={ALL_ROLES}><AdvanceCash /></ProtectedRoute>} />
        <Route path="analytics" element={<ProtectedRoute roles={ALL_ROLES}><Analytics /></ProtectedRoute>} />
        <Route path="crm-reports" element={<ProtectedRoute permKey="crm_view"><CrmReports /></ProtectedRoute>} />
        <Route path="handover-forms" element={<ProtectedRoute permKey="handover_write"><HandoverForms /></ProtectedRoute>} />
        <Route path="profile" element={<ProtectedRoute roles={ALL_ROLES}><Profile /></ProtectedRoute>} />
      </Route>
    </Routes>
  )
}
